import React, { useState } from "react";
import ReactQuill from "react-quill";
import "quill/dist/quill.snow.css";

const quillModules = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { header: [1, 2, 3, false] }],
    [{ font: [] }],
    [{ size: ["small", false, "large", "huge"] }],
    [{ align: [] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "image"],
    ["clean"],
  ],
};

const inputClass =
  "h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100";
const textAreaClass =
  "min-h-24 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100";

const formatLink = (title) =>
  String(title || "")
    .toLowerCase()
    .replace(/^https?:\/\/(www\.)?prepnexis\.com\/blog\//, "")
    .replace(/^\/?blog\//, "")
    .replace(/\s+/g, "-")
    .replace(/[^\w-]+/g, "");

const formatPreviewDate = (dateValue) => {
  const date = dateValue ? new Date(dateValue) : new Date();
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

const getPreviewImageSrc = (featuredImage) => {
  if (!featuredImage) return "";
  if (/^https?:\/\//i.test(featuredImage)) return featuredImage;
  return `${process.env.REACT_APP_DO_SPACES_URL || ""}${featuredImage}`;
};

const Field = ({ label, children, wide }) => (
  <label className={`block ${wide ? "lg:col-span-2" : ""}`}>
    <span className="text-sm font-semibold text-slate-700">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

export default function CreatePostForm({ post, onSubmit, onCancel, previewMode, setPreviewMode }) {
  const [newPost, setNewPost] = useState({
    title: post?.title || "",
    content: post?.content || "",
    excerpt: post?.excerpt || "",
    author: post?.author || "",
    featuredImage: post?.featuredImage || "",
    readMins: post?.readMins || 0,
    category: post?.category || "Latest",
    status: post?.status || "draft",
    link: post?.link || "",
    keywords: post?.keywords || "",
    description: post?.description || "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const uploadImage = async (file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append("image", file);

    try {
      const response = await fetch("/api/upload", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) throw new Error("Upload failed");

      const data = await response.json();

      if (data.fileKey) {
        const imageUrl = `/${data.fileKey}`;
        setNewPost((prev) => ({ ...prev, featuredImage: imageUrl }));
      } else {
        alert("Image upload failed: No fileKey returned from server.");
      }
    } catch (error) {
      console.error("Image upload error:", error);
      alert("Failed to upload image");
    }
  };

  const handleSubmit = () => {
    if (isSubmitting) return;

    if (!newPost.title || !newPost.content || !newPost.category || !newPost.readMins || !newPost.author || !newPost.excerpt) {
      alert("Please fill in all required fields.");
      return;
    }

    setIsSubmitting(true);
    const formattedLink = newPost.link ? formatLink(newPost.link) : formatLink(newPost.title);

    const postToSubmit = {
      ...newPost,
      link: formattedLink,
      featuredImage: newPost.featuredImage,
      keywords: newPost.keywords,
      description: newPost.description,
    };

    alert(post?._id ? "Post updated successfully" : "Post created successfully");
    onSubmit(postToSubmit);
    setIsSubmitting(false);
  };

  return (
    <section className="mb-6 rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-5 flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <h3 className="text-lg font-bold text-slate-950">{post?._id ? "Edit Post" : "Create Post"}</h3>
          <p className="text-sm text-slate-500">Write the post, tune SEO metadata, and preview before publishing.</p>
        </div>
        <div className="grid grid-cols-2 rounded-lg bg-slate-100 p-1">
          <button
            className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
              !previewMode ? "bg-white text-slate-950 shadow-sm" : "text-slate-500 hover:text-slate-950"
            }`}
            onClick={() => setPreviewMode(false)}
            type="button"
          >
            Editor
          </button>
          <button
            className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
              previewMode ? "bg-white text-slate-950 shadow-sm" : "text-slate-500 hover:text-slate-950"
            }`}
            onClick={() => setPreviewMode(true)}
            type="button"
          >
            Preview
          </button>
        </div>
      </div>

      {!previewMode && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Field label="Title" wide>
            <input
              type="text"
              placeholder="Post title"
              className={inputClass}
              value={newPost.title}
              onChange={(event) => setNewPost({ ...newPost, title: event.target.value })}
            />
          </Field>

          <Field label="Keywords">
            <input
              type="text"
              placeholder="keyword, keyword, keyword"
              className={inputClass}
              value={newPost.keywords || ""}
              onChange={(event) => setNewPost({ ...newPost, keywords: event.target.value })}
            />
          </Field>

          <Field label="Author">
            <input
              type="text"
              placeholder="Author"
              className={inputClass}
              value={newPost.author}
              onChange={(event) => setNewPost({ ...newPost, author: event.target.value })}
            />
          </Field>

          <Field label="Meta Description" wide>
            <textarea
              placeholder="Meta description"
              className={textAreaClass}
              value={newPost.description || ""}
              onChange={(event) => setNewPost({ ...newPost, description: event.target.value })}
            />
          </Field>

          <Field label="Excerpt" wide>
            <textarea
              placeholder="Short post excerpt"
              className={textAreaClass}
              value={newPost.excerpt}
              onChange={(event) => setNewPost({ ...newPost, excerpt: event.target.value })}
            />
          </Field>

          <Field label="Featured Image">
            <input
              type="file"
              accept="image/*"
              className="block w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-950 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-white"
              onChange={(event) => uploadImage(event.target.files[0])}
            />
          </Field>

          <Field label="Reading Time">
            <input
              type="number"
              placeholder="Minutes"
              className={inputClass}
              value={newPost.readMins}
              onChange={(event) => setNewPost({ ...newPost, readMins: event.target.value })}
            />
          </Field>

          {newPost.featuredImage && (
            <div className="lg:col-span-2 overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
              <img src={newPost.featuredImage} alt="Featured" className="max-h-72 w-full object-cover" />
            </div>
          )}

          <Field label="Category">
            <select
              className={inputClass}
              value={newPost.category}
              onChange={(event) => setNewPost({ ...newPost, category: event.target.value })}
            >
              <option value="Technology">Technology</option>
              <option value="Health">Health</option>
              <option value="Education">Education</option>
              <option value="Latest">Latest</option>
              <option value="Featured">Featured</option>
              <option value="News">News</option>
            </select>
          </Field>

          <Field label="Status">
            <select
              className={inputClass}
              value={newPost.status}
              onChange={(event) => setNewPost({ ...newPost, status: event.target.value })}
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </Field>

          <Field label="Link" wide>
            <input
              type="text"
              placeholder="URL slug"
              className={inputClass}
              value={newPost.link}
              onChange={(event) => setNewPost({ ...newPost, link: event.target.value })}
              onBlur={(event) => setNewPost({ ...newPost, link: formatLink(event.target.value) })}
            />
          </Field>

          <div className="lg:col-span-2">
            <span className="text-sm font-semibold text-slate-700">Content</span>
            <div className="mt-1 overflow-hidden rounded-lg border border-slate-200 bg-white">
              <ReactQuill
                value={newPost.content}
                onChange={(value) => setNewPost({ ...newPost, content: value })}
                modules={quillModules}
                className="min-h-72"
              />
            </div>
          </div>
        </div>
      )}

      {previewMode && (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <div className="border-b border-slate-200 bg-slate-50 px-5 py-3">
            <span className="text-xs font-semibold uppercase tracking-wide text-sky-600">
              Website blog page preview
            </span>
          </div>

          <div className="max-w-3xl px-4 py-6 md:mx-36">
            <header className="mb-6">
              <h1 className="text-4xl font-bold leading-tight text-gray-900 md:text-5xl">
                {newPost.title || "Untitled post"}
              </h1>
              {newPost.excerpt && <p className="mt-2 text-2xl text-gray-500">{newPost.excerpt}</p>}
            </header>

            <div className="flex flex-col items-start justify-between gap-3 border-b pb-4 text-sm text-gray-500 md:flex-row md:items-center">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-gray-800">{newPost.category || "Latest"}</span>
                <span>&middot;</span>
                <span>{newPost.readMins || "N/A"} mins</span>
                <span>&middot;</span>
                <time>{formatPreviewDate(post?.date || post?.createdAt || post?.updatedAt)}</time>
              </div>
              <div className="flex items-center">
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/b/b3/Eo_circle_purple_white_letter-n.svg"
                  alt="Author"
                  className="h-8 w-8 rounded-full"
                />
                <div className="ml-2">
                  <span className="text-gray-700">Written by </span>
                  <span className="text-fuchsia-600">{newPost.author || "Admin"}</span>
                </div>
              </div>
            </div>

            {newPost.featuredImage && (
              <div className="mt-6">
                <img
                  src={getPreviewImageSrc(newPost.featuredImage)}
                  alt={newPost.title || "Featured"}
                  className="w-full rounded-lg shadow-md"
                />
              </div>
            )}
          </div>

          <div
            className="prose max-w-3xl px-4 py-6 text-lg font-normal leading-[1.75] text-gray-700 md:mx-36"
            dangerouslySetInnerHTML={{ __html: newPost.content }}
          />
        </div>
      )}

      <div className="mt-5 flex flex-col-reverse justify-end gap-3 sm:flex-row">
        <button
          className="rounded-lg border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
          onClick={onCancel}
          type="button"
        >
          Cancel
        </button>
        <button
          className="rounded-lg bg-sky-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
          onClick={handleSubmit}
          disabled={isSubmitting}
          type="button"
        >
          {post?._id ? "Update Post" : "Create Post"}
        </button>
      </div>
    </section>
  );
}
